import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiCommand, FiSearch, FiX } from "react-icons/fi";
import { Button, EmptyState, Input } from "@swot/base-components";
import { getVisibleCmtsCommands, type CmtsCommandDefinition } from "./cmtsCommandRegistry.js";
import { throwCmtsCanonicalError } from "@cmts/api/cmtsApiErrors.js";

export function CmtsCommandCenter(props: {
  open: boolean;
  permissions?: readonly string[] | undefined;
  onClose: () => void;
  onNavigate: (path: string) => void;
  onOpenNotifications?: (() => void) | undefined;
}) {
  const [query, setQuery] = useState("");
  const panelRef = useRef<HTMLElement | null>(null);
  const commands = useMemo(() => {
    const q = query.trim().toLowerCase();
    const visible = getVisibleCmtsCommands(props.permissions);
    if (!q) return visible;
    return visible.filter((command) => [command.label, command.description, ...command.keywords].join(" ").toLowerCase().includes(q));
  }, [props.permissions, query]);

  useEffect(() => {
    if (!props.open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        props.onClose();
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target && panelRef.current && !panelRef.current.contains(target)) props.onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [props.open, props.onClose]);

  if (!props.open || typeof document === "undefined") return null;

  const execute = (command: CmtsCommandDefinition) => {
    if (command.commandType === "overlay" && command.actionKind === "drawer" && command.id === "notifications") {
      props.onClose();
      if (!props.onOpenNotifications) throwCmtsCanonicalError({ code: "CMTS_ERR_SIDE_EFFECT_UNAVAILABLE", message: "Notification command is not connected to the CMTS notification center drawer action.", details: { commandId: command.id, commandType: command.commandType, actionKind: command.actionKind, path: command.path } });
      props.onOpenNotifications();
      return;
    }
    props.onNavigate(command.path);
    props.onClose();
  };

  return createPortal(
    <div className="cmts-command-backdrop" role="presentation" data-overlay-dismiss="command-center">
      <section ref={panelRef} className="cmts-command-center" role="dialog" aria-modal="true" aria-labelledby="cmts-command-title" onPointerDown={(event) => event.stopPropagation()}>
        <header className="cmts-command-center__header">
          <div className="cmts-command-center__title">
            <FiCommand aria-hidden="true" />
            <div>
              <p className="cmts-section-eyebrow">Enterprise Command Center</p>
              <h2 id="cmts-command-title">Search and execute CMTS actions</h2>
              <span>Only actions allowed for this persona and role scope are shown.</span>
            </div>
          </div>
          <Button label="Close" variant="ghost" aria-label="Close command center" onClick={props.onClose} leftIcon={<FiX aria-hidden="true" />} />
        </header>
        <div className="cmts-command-search">
          <Input label="Search" value={query} onChange={(event) => setQuery(event.currentTarget.value)} placeholder="Search complaints, SLA, RCA, CAPA, reports, tasks..." />
        </div>
        <div className="cmts-command-grid" role="listbox" aria-label="Available CMTS commands">
          {commands.map((command) => {
            const Icon = command.icon;
            return (
              <Button key={command.id} className="cmts-command-card" variant="ghost" onClick={() => execute(command)}>
                <span className="cmts-command-card__icon"><Icon aria-hidden="true" /></span>
                <span className="cmts-command-card__body"><strong>{command.label}</strong><small>{command.description}</small></span>
              </Button>
            );
          })}
          {commands.length === 0 ? <EmptyState title="No commands available" message="No commands match your search or permission scope." /> : null}
        </div>
      </section>
    </div>,
    document.body,
  );
}
